import { Box, TextField, MenuItem } from '@mui/material';

export interface UnitValue {
  amount: string | number | null;
  unit: string;
}

interface UnitValueFieldProps {
  label: string;
  value: UnitValue | null;
  /** List of unit strings (parameterTags). Pass empty array to hide the unit selector. */
  units: string[];
  onChange: (v: UnitValue) => void;
  /** Controls whether the amount input renders as "number" or "text". Defaults to "text". */
  inputType?: 'text' | 'number';
  required?: boolean;
  placeholder?: string;
}

function hasValidUnits(units: string[]): boolean {
  return units.some((u) => u && u !== 'none' && u.trim() !== '');
}

/**
 * Reusable amount + unit-select field.
 * Mirrors Angular's `unit-amount-group` input group pattern from step-parameters.
 */
export function UnitValueField({
  label,
  value,
  units,
  onChange,
  inputType = 'text',
  required = false,
  placeholder,
}: UnitValueFieldProps) {
  const current: UnitValue = value ?? { amount: '', unit: '' };
  const showUnits = hasValidUnits(units);

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
      <TextField
        size="small"
        label={label + (required ? ' *' : '')}
        type={inputType}
        value={current.amount ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          const amount =
            inputType === 'number' ? (raw === '' ? null : Number(raw)) : raw;
          onChange({ ...current, amount });
        }}
        sx={{ flex: 1 }}
        slotProps={{
          input: {
            placeholder: placeholder,
            ...(inputType === 'number' ? { min: 0, step: 'any' } : {})
          },
          ...(inputType === 'number' ? { htmlInput: { min: 0, step: 'any' } } : {})
        }}
      />
      {showUnits && (
        <TextField
          select
          size="small"
          label="Unit"
          value={current.unit ?? ''}
          onChange={(e) => onChange({ ...current, unit: e.target.value })}
          sx={{ minWidth: 88 }}
        >
          <MenuItem value="">Unit</MenuItem>
          {units
            .filter((u) => u && u !== 'none' && u.trim() !== '')
            .map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
        </TextField>
      )}
    </Box>
  );
}
